import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Trash2, Link, Mail, Phone, FileText } from 'lucide-react';
import { StakeholderThumbnail } from './StakeholderThumbnail';
import { STATUS_OPTIONS, STATUS_CONFIG, DEFAULT_STATUS, type StakeholderStatus } from '@/constants/stakeholderStatus';
import type { Stakeholder } from '@/pages/admin/StakeholderManagement';

interface StakeholderGridViewProps {
  stakeholders: Stakeholder[];
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (stakeholder: Stakeholder) => void;
  onDelete: (stakeholder: Stakeholder) => void;
  onAssign: (stakeholder: Stakeholder) => void;
  onViewNote: (stakeholder: Stakeholder) => void;
  onStatusChange?: (stakeholderId: string, newStatus: string) => void;
  onImageEdit?: (stakeholder: Stakeholder) => void;
  onProjectEdit?: (stakeholder: Stakeholder) => void;
}

export const StakeholderGridView = ({ 
  stakeholders, 
  canEdit, 
  canDelete, 
  onEdit, 
  onDelete, 
  onAssign,
  onViewNote,
  onStatusChange,
  onImageEdit,
  onProjectEdit
}: StakeholderGridViewProps) => {
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);

  const handleStatusClick = (stakeholderId: string) => {
    if (!canEdit || !onStatusChange) return;
    setEditingStatusId(stakeholderId);
  };

  const handleStatusSelect = (stakeholderId: string, newStatus: string) => {
    onStatusChange?.(stakeholderId, newStatus);
    setEditingStatusId(null);
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 p-4">
      {stakeholders.map((stakeholder, index) => (
        <Card 
          key={stakeholder.id} 
          className="p-3 flex flex-col gap-2 hover:shadow-lg transition-shadow min-h-0"
        >
          {/* Avatar/Thumbnail - center top */}
          <div className="flex justify-center">
            <StakeholderThumbnail 
              attachments={stakeholder.attachments} 
              stakeholderId={stakeholder.id}
              index={index}
              size="medium"
              editable={canEdit && !!onImageEdit}
              onEdit={() => onImageEdit?.(stakeholder)}
            />
          </div>

          {/* Status Badge + Name */}
          <div className="text-center grid gap-1 min-h-[2.5rem]">
            {/* Status Badge - Inline Editable */}
            {editingStatusId === stakeholder.id ? (
              <Select
                value={stakeholder.status || 'hold'}
                onValueChange={(value) => handleStatusSelect(stakeholder.id, value)}
                onOpenChange={(open) => !open && setEditingStatusId(null)}
              >
                <SelectTrigger className="h-6 w-[90px] mx-auto text-[10px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-1.5">
                          {Icon && <Icon className="h-3 w-3" />}
                          {opt.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              (() => {
                const status = (stakeholder.status || DEFAULT_STATUS) as StakeholderStatus;
                const config = STATUS_CONFIG[status] || STATUS_CONFIG[DEFAULT_STATUS];
                const Icon = config.icon;
                return (
                  <Badge
                    variant={config.activeClassName ? undefined : config.variant}
                    className={`text-[10px] py-0 px-1.5 mx-auto ${config.activeClassName ?? ''} ${canEdit && onStatusChange ? 'cursor-pointer hover:ring-1 hover:ring-primary/50' : ''}`}
                    onClick={() => handleStatusClick(stakeholder.id)}
                    title={canEdit && onStatusChange ? 'Click to change status' : ''}
                  >
                    <span className="flex items-center gap-1">
                      {Icon && <Icon className="h-2.5 w-2.5" />}
                      {config.label}
                    </span>
                  </Badge>
                );
              })()
            )}
            <h3 
              className="font-semibold text-sm leading-snug line-clamp-2 overflow-hidden break-words cursor-help"
              title={stakeholder.name}
            >
              {stakeholder.name}
            </h3>
          </div>

          {/* Contact Info */}
          <div className="space-y-1 text-xs text-muted-foreground h-[2.5rem] overflow-hidden min-h-0">
            {stakeholder.email && (
              <div className="flex items-center gap-1 truncate" title={stakeholder.email}>
                <Mail className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{stakeholder.email}</span>
              </div>
            )}
            {stakeholder.phone && (
              <div className="flex items-center gap-1" title={stakeholder.phone}>
                <Phone className="h-3 w-3 flex-shrink-0" />
                <span>{stakeholder.phone}</span>
              </div>
            )}
          </div>

          {/* Badges Section - Fixed 2 rows with grid */}
          <div className="grid gap-1.5 grid-rows-[1.25rem_1.25rem] min-h-0">
            {/* Row 1: Group */}
            <div className="w-full h-[1.25rem] min-h-0 flex items-center justify-center overflow-hidden">
              {stakeholder.project_group ? (
                <Badge variant="secondary" className="text-xs whitespace-nowrap truncate max-w-[90%] py-0 leading-none">
                  {stakeholder.project_group}
                </Badge>
              ) : (
                <span className="invisible text-xs leading-none">placeholder</span>
              )}
            </div>

            {/* Row 2: Type */}
            <div className="w-full h-[1.25rem] min-h-0 flex items-center justify-center overflow-hidden">
              {stakeholder.project_type ? (
                <Badge variant="outline" className="text-xs whitespace-nowrap truncate max-w-[90%] py-0 leading-none">
                  {stakeholder.project_type}
                </Badge>
              ) : (
                <span className="invisible text-xs leading-none">placeholder</span>
              )}
            </div>
          </div>

          {/* Project Badge - Fixed at bottom, differentiate linked vs text */}
          <div className="mt-auto pt-2 border-t">
            {(() => {
              const linkedProject = stakeholder.project_name;
              const textProject = (stakeholder as any).text_project;
              
              if (stakeholder.project_id && linkedProject) {
                // Linked project - show with Link icon (same line)
                return (
                  <Badge variant="secondary" className="text-xs flex items-center justify-center text-foreground font-medium w-full bg-secondary/80 gap-1 whitespace-nowrap overflow-hidden">
                    <Link className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{linkedProject}</span>
                  </Badge>
                );
              }
              
              if (textProject) {
                // Text-only project - muted style, clickable to edit
                return (
                  <Badge 
                    variant="outline" 
                    className={`text-xs justify-center line-clamp-1 w-full text-muted-foreground ${canEdit && onProjectEdit ? 'cursor-pointer hover:bg-secondary' : ''}`}
                    onClick={() => canEdit && onProjectEdit?.(stakeholder)}
                    title={canEdit && onProjectEdit ? 'Click to edit project name' : textProject}
                  >
                    {textProject}
                  </Badge>
                );
              }
              
              // Empty - clickable to add
              return (
                <Badge 
                  variant="outline" 
                  className={`text-xs justify-center opacity-40 text-muted-foreground w-full ${canEdit && onProjectEdit ? 'cursor-pointer hover:bg-secondary hover:opacity-70' : ''}`}
                  onClick={() => canEdit && onProjectEdit?.(stakeholder)}
                  title={canEdit && onProjectEdit ? 'Click to add project name' : ''}
                >
                  No project
                </Badge>
              );
            })()}
          </div>

          {/* Action buttons */}
          {(canEdit || canDelete) && (
            <div className="flex gap-1 justify-start items-center pt-1.5 flex-wrap">
              {/* Lead Avatar */}
              {stakeholder.creator_name && (
                <Avatar className="h-8 w-8" title={`Lead: ${stakeholder.creator_name}`}>
                  <AvatarImage 
                    src={stakeholder.creator_avatar_url || undefined}
                    fallbackName={stakeholder.creator_name}
                  />
                  <AvatarFallback className="text-[10px]">
                    {stakeholder.creator_name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              )}
              
              {/* Notes button - only show if has notes */}
              {stakeholder.notes && (
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onViewNote(stakeholder)}
                  title={stakeholder.notes.length > 50 ? stakeholder.notes.substring(0, 50) + '...' : stakeholder.notes}
                >
                  <FileText className="h-4 w-4" />
                </Button>
              )}
              
              {canEdit && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onEdit(stakeholder)}
                  title="Edit attachments, interactions, and history"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              {canEdit && !stakeholder.project_id && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onAssign(stakeholder)}
                  title="Assign to project"
                >
                  <Link className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};
